import type { AxePurchaseState, v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import type { FeatureFlag } from '~/common/contexts/featureFlags';
import { AXE_DEVELOPER_HUB_DOCS_URL } from '../axe-watcher/constants';

/** The Account Portal pseudo-product, which is not part of the billing `ProductSlugs` enum. */
export const AXE_ACCOUNT_PRODUCT_SLUG = 'axe-account';

/**
 * All billing product slugs plus the Account Portal pseudo-product. Shared by the
 * announcement admin form and the feature-flag product select.
 */
export const PRODUCT_SLUGS_WITH_ACCOUNT: readonly string[] = [
  ...Object.values(ProductSlugs),
  AXE_ACCOUNT_PRODUCT_SLUG
];

export const PROD_EXTENSION_URL =
  'https://chrome.google.com/webstore/detail/axe-web-accessibility-tes/lhdoppojpmngadmnindnejefpokejbdd';
export const EXTENSION_HEALTH_CHECK = 'extension:healthcheck';
export const EXTENSION_HEALTH_CHECK_RESPONSE = 'extension:healthcheck:response';
export const EXTENSION_HEALTH_CHECK_TIMEOUT = 1000;
export const EXTENSION_LAUNCH_CUSTOM_INTEGRATION =
  'extension:custom-integration:launch';
export const INAPPLICABLE_SIGNUP_PASSWORD_REQUIREMENTS = [
  'forceExpiredPasswordChange',
  'passwordHistory',
  'passwordBlacklist',
  'regexPattern',
  'hashIterations',
  'hashAlgorithm'
];

// Taken from https://github.com/dequelabs/axe-extension/blob/a89b73a3f52c4f1aa701727a895595f265118c10/src/shared/user-roles.js
export const USER_JOB_ROLES = [
  'Developer',
  'Tester',
  'Designer',
  'Accessibility Specialist',
  'Manager',
  'Other'
] as const;

export const PRO_FEATURES = [
  'Automated testing',
  'Intelligent Guided Testing',
  'Test individual components',
  'Save & Export results',
  'Machine learning'
];

export const DOWNGRADABLE_STATES: Readonly<AxePurchaseState>[] = [
  'paid',
  'paid_payment_failed',
  'trial_payment_failed',
  'free_payment_failed',
  'trial_ended'
];

export const DEFAULT_LANGUAGE = 'en';

export const FREE_STATES: Readonly<AxePurchaseState>[] = [
  'free',
  'trial_ended',
  'trial_payment_failed',
  'free_payment_failed',
  'paid_payment_failed'
];

export const TRIAL_EXPIRED_STATES: Readonly<AxePurchaseState>[] = [
  'trial_ended'
];

export const TRIAL_STATES: Readonly<AxePurchaseState>[] = [
  'trialing',
  'trial_ended'
];

export const ACTIVE_STATES: Readonly<AxePurchaseState>[] = ['paid', 'trialing'];

export const FAILED_PAYMENT_STATES: Readonly<AxePurchaseState>[] = [
  'free_payment_failed',
  'trial_payment_failed',
  'paid_payment_failed'
];

export const SUPPORTED_TRIAL_PRODUCT_SLUGS = window.appConfig
  .SUPPORTED_TRIAL_PRODUCT_SLUGS
  ? (window.appConfig.SUPPORTED_TRIAL_PRODUCT_SLUGS.split(
      ','
    ) as ProductSlugs[])
  : [
      ProductSlugs.axeLinter,
      ProductSlugs.axeDevToolsExtension,
      // Temporarily disabling free trials for mobile
      // ProductSlugs.axeDevToolsMobile,
      ProductSlugs.axeSpider
    ];

/*
TODO - Integration
Delete SupportedIntegrationProductSlugs - use regular ProductSlugs
*/
export enum SupportedIntegrationProductSlugs {
  jiraDirectCloudIntegration = ProductSlugs.jiraDirectCloudIntegration,
  jiraOnPremIntegration = ProductSlugs.jiraOnPremIntegration,
  customIntegration = ProductSlugs.customIntegration
}

export const SupportedShareableIssueIntegrationProductSlugs: SupportedIntegrationProductSlugs[] =
  [
    SupportedIntegrationProductSlugs.jiraDirectCloudIntegration,
    SupportedIntegrationProductSlugs.jiraOnPremIntegration
  ];

export const INTEGRATION_PRODUCT_SLUGS: SupportedIntegrationProductSlugs[] =
  Object.values(SupportedIntegrationProductSlugs);

export const PRODUCTS_WITHOUT_PLAN_NAME: string[] = [
  ProductSlugs.axeMonitor,
  ProductSlugs.axeReports,
  ProductSlugs.axeAuditor,
  ProductSlugs.jiraIntegration,
  ProductSlugs.axeAuditorIntegration,
  ProductSlugs.axeAssistantSlack,
  ProductSlugs.axeAssistantTeams
];

export const hasPlanName = (product_slug?: string) =>
  !(product_slug && PRODUCTS_WITHOUT_PLAN_NAME.includes(product_slug));

export enum ProductNames {
  'jira' = 'Jira',
  'axe' = 'Axe',
  'devtools' = 'DevTools',
  'mobile' = 'Axe DevTools for Mobile',
  'developerHub' = 'Axe Developer Hub'
}

export const COMPANY_NAME = 'Deque';

export const AXE_ACCOUNT_TEAMS_URL =
  'https://docs.deque.com/devtools-server/4.0.0/en/teams';

export const AXE_MCP_SERVER_DOCS_URL =
  'https://docs.deque.com/devtools-server/4.0.0/en/axe-mcp-server';
export const AXE_MCP_SERVER_NPM_URL =
  'https://www.npmjs.com/package/axe-mcp-server';
export const AXE_MCP_SERVER_DOCKER_HUB_URL =
  'https://hub.docker.com/r/dequesystems/axe-mcp-server';

export type SupportedTrialProductSlugs =
  (typeof SUPPORTED_TRIAL_PRODUCT_SLUGS)[number];

export const isSubscriptionExpired = (
  subscription: v2.EnterpriseSubscription | v2.UserSubscription
) => FREE_STATES.includes(subscription.purchase_state as AxePurchaseState);

interface AuditorProductUrls {
  tests?: string;
  issues?: string;
  reports?: string;
  admin?: string;
}
export interface ProductUrls extends AuditorProductUrls {
  docs?: string;
  docsAnalyzer?: string;
  docsSDK?: string;
  signup?: string;
  root?: string;
  gettingStarted?: string;
  plans?: string;
  trial?: string;
  welcome?: string;
  [key: string]: string | undefined;
}

export const PRODUCT_URLS = {
  [ProductSlugs.axeDevToolsExtension]: {
    root: '/axe-devtools',
    plans: '/axe-devtools/plans',
    docs: 'https://axe-devtools-html-docs.deque.com/extensions/devtools/home.html',
    trial: `/${ProductSlugs.axeDevToolsExtension}/trial`,
    welcome: '/axe-devtools',
    signup: `/signup?product=${ProductSlugs.axeDevToolsExtension}`,
    notInstalled: `/axe-devtools/not-installed`
  },
  [ProductSlugs.axeDevToolsMobile]: {
    root: `/${ProductSlugs.axeDevToolsMobile}`,
    docsSDK: 'https://docs.deque.com/devtools-mobile',
    docsAnalyzer: 'https://docs.deque.com/devtools-mobile/en/mobile-analyzer',
    trial: `/${ProductSlugs.axeDevToolsMobile}/get-started`,
    dashboard: window.appConfig.MOBILE_APP_URL || '',
    signup: `/signup?product=${ProductSlugs.axeDevToolsMobile}`,
    automated: `/${ProductSlugs.axeDevToolsMobile}/automated-testing-sdk`,
    analyzer: `/${ProductSlugs.axeDevToolsMobile}/running-mobile-app-analyzer`
  },
  [ProductSlugs.axeDevToolsWatcher]: {
    root: '/axe-watcher',
    docs: AXE_DEVELOPER_HUB_DOCS_URL,
    startup: '/axe-watcher/startup',
    startFreePlan: '/axe-watcher/start-free-plan',
    trial: '/axe-watcher/startup',
    projects: '/axe-watcher/projects',
    issues: '/axe-watcher/issues',
    signup: `/signup?product=${ProductSlugs.axeDevToolsWatcher}`
  },
  [ProductSlugs.axeLinter]: {
    plans: 'https://www.deque.com/axe/devtools/',
    docs: 'https://docs.deque.com/linter/1.0.0/en/linter-home',
    trial: `/${ProductSlugs.axeLinter}/trial`,
    signup: `/signup?product=${ProductSlugs.axeLinter}`
  },
  [ProductSlugs.axeAuditor]: {
    root: `/${ProductSlugs.axeAuditor}`,
    tests: `/${ProductSlugs.axeAuditor}`,
    issues: `/${ProductSlugs.axeAuditor}/issues`,
    reports: `/${ProductSlugs.axeAuditor}/reports`,
    docs: 'https://docs.deque.com/auditor',
    signup: `/signup?product=${ProductSlugs.axeAuditor}`
  },
  [ProductSlugs.dequeUniversity]: {
    site: window.appConfig.DEQUE_UNIVERSITY_URL || '',
    docs: (window.appConfig.DEQUE_UNIVERSITY_URL || '') + '/support',
    plans: (window.appConfig.DEQUE_UNIVERSITY_URL || '') + '/online-courses',
    signup: `/signup?product=${ProductSlugs.dequeUniversity}`
  },
  [ProductSlugs.axeSpider]: {
    root: `/${ProductSlugs.axeSpider}`,
    signup: `/signup?product=${ProductSlugs.axeSpider}`,
    trial: `/${ProductSlugs.axeSpider}/trial`,
    createProject: `/${ProductSlugs.axeSpider}/projects/new`,
    projects: `/${ProductSlugs.axeSpider}/projects`
  },
  [ProductSlugs.jiraDirectCloudIntegration]: {
    root: `/configuration/integrations`,
    docs: 'https://docs.deque.com/integrations/1/en/jira'
  },
  [ProductSlugs.jiraOnPremIntegration]: {
    root: `/configuration/integrations`,
    docs: 'https://docs.deque.com/integrations/1/en/jira'
  },
  [ProductSlugs.axeMonitor]: {
    docs: 'https://docs.deque.com/monitor'
  },
  [ProductSlugs.axeReports]: {
    docs: 'https://docs.deque.com/reports'
  },
  [ProductSlugs.axeAuditorIntegration]: {
    docs: 'https://docs.deque.com/auditor/2.24/en/release-summary224#integration-with-axe-devtools-pro-extension'
  },
  [ProductSlugs.axeAssistantSlack]: {
    docs: 'https://docs.deque.com/assistant',
    info: 'https://dequeuniversity.com/introducing-axe-assistant',
    install: window.appConfig.AXE_ASSISTANT_SLACK_INSTALL_LINK
  },
  [ProductSlugs.axeAssistantTeams]: {
    docs: 'https://docs.deque.com/assistant',
    info: 'https://dequeuniversity.com/introducing-axe-assistant'
  },
  [ProductSlugs.customIntegration]: {
    root: `/configuration/integrations`,
    docs: 'https://docs.deque.com/integrations/1/en/custom-integration'
  },
  // NOTE: Intentionally does NOT include `welcome` or `plans` keys. `SignUp.tsx`'s
  // post-signup redirect logic only falls through to the raw `redirect_uri` (which is
  // how signup lands on the axe-mcp-server#734 getting-started page at `gettingStarted`)
  // when `getProductUrl(ProductSlugs.axeMCPServer, 'welcome' | 'plans')` is `undefined`.
  // Adding either key here would silently hijack that redirect. `root` only redirects
  // to `gettingStarted`.
  [ProductSlugs.axeMCPServer]: {
    root: '/axe-mcp-server',
    signup: `/signup?product=${ProductSlugs.axeMCPServer}`,
    gettingStarted: '/axe-mcp-server/getting-started'
  }
} as const satisfies Partial<Record<ProductSlugs, ProductUrls>>;

export enum HubSpotProperties {
  AXE_PAID_DATE = 'axe_paid_date',
  AXE_PAID_DATE_END = 'axe_paid_date_end',
  AXE_FIRST_TEST_SAVED_DATE = 'axe_first_test_saved_date',
  AXE_FIRST_EXPORT_DATE = 'axe_first_export_date',
  AXE_FIRST_SCOPED_TEST_DATE = 'axe_first_scoped_test_date',
  AXE_ENTERPRISE_DATE = 'axe_enterprise_date',
  AXE_DOWNGRADED_FROM_TRIAL_DATE = 'axe_downgraded_from_trial_date',
  AXE_DOWNGRADED_FROM_PAID_DATE = 'axe_downgraded_from_paid_date',
  AXE_TRIAL_STARTED_DATE = 'axe_trial_started_date',
  AXE_FIRST_IGT_COMPLETED_DATE = 'axe_first_igt_completed_date',
  AXE_SIGNUP = 'axe_signup',
  REFERRER_EMAIL = 'referrer_email',
  REFERRAL_PURCHASE_DATE = 'referral_purchase_date',
  AXE_FIRST_LOGIN_DATE = 'axe_first_login_date',
  AXE_KEYCLOAK_ID = 'axe_keycloak_id',
  HS_MARKETABLE_STATUS = 'hs_marketable_status',
  AXE_LAST_LOGIN_DATE = 'axe_last_login_date',
  AXE_START_WALKTHROUGH_DATE = 'axe_start_walkthrough_date',
  AXE_NEXT_WALKTHROUGH_DATE = 'axe_next_walkthrough_date',
  AXE_SKIP_WALKTHROUGH_DATE = 'axe_skip_walkthrough_date',
  AXE_COMPLETED_WALKTHROUGH_DATE = 'axe_completed_walkthrough_date',
  ORIGINAL_LANDING_PAGE = 'original_landing_page',
  ORIGINAL_UTM_SOURCE = 'original_utm_source'
}

export const HIDDEN_USER_MANAGEMENT_PRODUCTS: ProductSlugs[] = [
  ProductSlugs.dequeUniversity
];

// Products that are integration or service-level subscriptions without per-user
// management. These should not appear in the "Add User" form.
// See: https://github.com/dequelabs/walnut/issues/13853
export const PRODUCTS_WITHOUT_USER_MANAGEMENT: ProductSlugs[] = [
  ProductSlugs.jiraIntegration,
  ProductSlugs.jiraDirectCloudIntegration,
  ProductSlugs.jiraOnPremIntegration,
  ProductSlugs.customIntegration,
  ProductSlugs.axeAuditorIntegration,
  ProductSlugs.axeAssistantSlack,
  ProductSlugs.axeAssistantTeams,
  ProductSlugs.advancedRules,
  ProductSlugs.scimService
];

export const getProductsWithAccessLevelManagement = (
  featureFlags: FeatureFlag[]
): ProductSlugs[] => {
  const products: ProductSlugs[] = [
    ProductSlugs.axeMonitor,
    ProductSlugs.axeReports
  ];

  const hasAuditorUserManagement = featureFlags.find(
    f => f.id === 'axe_auditor_user_management_v1'
  )?.state;

  if (hasAuditorUserManagement) {
    products.push(ProductSlugs.axeAuditor);
  }

  return products;
};

export const productHasAccessLevelManagement = (
  slug: ProductSlugs,
  featureFlags: FeatureFlag[]
) => getProductsWithAccessLevelManagement(featureFlags).includes(slug);

type KeyOfProductUrls = keyof typeof PRODUCT_URLS;

export const getProductUrl = (
  slug: ProductSlugs,
  type: keyof ProductUrls
): string | undefined =>
  (PRODUCT_URLS[slug as KeyOfProductUrls] as ProductUrls)?.[type];

export const isKeyOfProductUrls = (
  value: unknown
): value is KeyOfProductUrls => {
  return (
    typeof value === 'string' &&
    Object.keys(PRODUCT_URLS).includes(value as KeyOfProductUrls)
  );
};

export const originalRedirectQueryParam = 'original_redirect_uri';

export const postAuthRedirectQueryParam = 'redirect_url';

export const pendingPostAuthRedirectStorageKey = 'pending_post_auth_redirect';

export { ProductSlugs };

export const WALKTHROUGH_EVENTS = {
  checkOpen: 'walkthrough:check:open',
  opened: 'walkthrough:opened',
  scanned: 'walkthrough:scanned',
  saved: 'walkthrough:saved',
  igtCompleted: 'walkthrough:igt:completed'
};

export const MAX_TEAM_NAME_LENGTH = 128;

export enum OverviewPollingState {
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR'
}

export enum ScreenshotStatus {
  Loading = 'loading',
  Error = 'error',
  Complete = 'complete'
}

export const DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm:ssA';
